import { Suspense, lazy, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';

import { ErrorApi, api } from '../api/cliente';
import type { Pagina } from '../api/cliente';
import { useSesion } from '../auth/sesion';
import {
  Aviso,
  AreaTexto,
  Campo,
  Cargando,
  Casilla,
  Encabezado,
  Etiqueta,
  Seleccion,
  Tarjeta,
  Vacio,
  pesos,
} from '../ui/piezas';

const SelectorUbicacion = lazy(() => import('../ui/SelectorUbicacion'));

/**
 * Un proyecto de obra nueva, sus plantas y sus unidades.
 *
 * Existía entero en la API y no había forma de llegar a él sin escribir las
 * peticiones a mano. Lo que de verdad faltaba es lo último: nadie carga
 * doscientos apartamentos uno por uno, así que sin la generación en bloque el
 * módulo estaba pero no se usaba.
 */

interface Tipologia {
  id: string;
  nombre: string;
  descripcion: string | null;
  areaConstruida: number | null;
  areaPrivada: number | null;
  habitaciones: number | null;
  banos: number | null;
  garajes: number | null;
  /** La API la manda como `totalUnidades`; con `unidades` no llegaba nunca. */
  totalUnidades: number | null;
  unidadesDisponibles: number | null;
  activa: boolean;
}

interface Proyecto {
  id: string;
  nombre: string;
  slug: string;
  descripcion: string | null;
  constructora: string | null;
  sedeId: string;
  ciudadId: string;
  direccion: string | null;
  latitud: number | null;
  longitud: number | null;
  fechaEntregaEstimada: string | null;
  salaVentasDireccion: string | null;
  salaVentasTelefono: string | null;
  destacado: boolean;
  estadoPublicacion: string;
  tipologias: Tipologia[];
}

interface Ciudad {
  id: string;
  nombre: string;
}
interface Sede {
  id: string;
  nombre: string;
}
interface Unidad {
  id: string;
  codigo: string;
  titulo: string;
  precio: number | null;
  estadoComercial: string;
}

const OPERACIONES = [
  { valor: 'VENTA', texto: 'Venta' },
  { valor: 'ARRIENDO', texto: 'Arriendo' },
];

/* Los destinos que la API acepta desde cada estado de publicación. */
const SIGUIENTES: Record<string, string[]> = {
  BORRADOR: ['EN_REVISION', 'PUBLICADO', 'ARCHIVADO'],
  EN_REVISION: ['PUBLICADO', 'BORRADOR', 'ARCHIVADO'],
  PUBLICADO: ['BORRADOR', 'ARCHIVADO'],
  ARCHIVADO: ['BORRADOR'],
};

interface Forma {
  nombre: string;
  descripcion: string;
  constructora: string;
  sedeId: string;
  ciudadId: string;
  direccion: string;
  latitud: string;
  longitud: string;
  fechaEntregaEstimada: string;
  salaVentasDireccion: string;
  salaVentasTelefono: string;
  destacado: boolean;
}

const VACIA: Forma = {
  nombre: '',
  descripcion: '',
  constructora: '',
  sedeId: '',
  ciudadId: '',
  direccion: '',
  latitud: '',
  longitud: '',
  fechaEntregaEstimada: '',
  salaVentasDireccion: '',
  salaVentasTelefono: '',
  destacado: false,
};

function texto(valor: string): string | null {
  const limpio = valor.trim();
  return limpio === '' ? null : limpio;
}

function numero(valor: string): number | null {
  const limpio = valor.trim();
  if (limpio === '') return null;
  const leido = Number(limpio);
  return Number.isFinite(leido) ? leido : null;
}

export default function ProyectoFicha() {
  const { id } = useParams();
  const navegar = useNavigate();
  const { puede } = useSesion();
  const cliente = useQueryClient();
  const esNuevo = !id;

  const [forma, setForma] = useState<Forma>(VACIA);
  const [error, setError] = useState<unknown>(null);
  const [exito, setExito] = useState<string | null>(null);

  const ficha = useQuery({
    queryKey: ['proyecto', id],
    queryFn: () => api<Proyecto>(`/admin/proyectos/${id}`),
    enabled: !esNuevo,
  });

  const ciudades = useQuery({
    queryKey: ['ciudades'],
    queryFn: () => api<Ciudad[]>('/public/geografia/ciudades'),
  });
  const sedes = useQuery({
    queryKey: ['sedes'],
    queryFn: () => api<Sede[]>('/admin/sedes'),
  });

  useEffect(() => {
    const p = ficha.data;
    if (!p) return;
    setForma({
      nombre: p.nombre,
      descripcion: p.descripcion ?? '',
      constructora: p.constructora ?? '',
      sedeId: p.sedeId,
      ciudadId: p.ciudadId,
      direccion: p.direccion ?? '',
      latitud: p.latitud != null ? String(p.latitud) : '',
      longitud: p.longitud != null ? String(p.longitud) : '',
      fechaEntregaEstimada: p.fechaEntregaEstimada ?? '',
      salaVentasDireccion: p.salaVentasDireccion ?? '',
      salaVentasTelefono: p.salaVentasTelefono ?? '',
      destacado: p.destacado,
    });
  }, [ficha.data]);

  const cuerpo = () => ({
    nombre: forma.nombre.trim(),
    descripcion: texto(forma.descripcion),
    constructora: texto(forma.constructora),
    sedeId: forma.sedeId,
    ciudadId: forma.ciudadId,
    direccion: texto(forma.direccion),
    latitud: numero(forma.latitud),
    longitud: numero(forma.longitud),
    fechaEntregaEstimada: texto(forma.fechaEntregaEstimada),
    salaVentasDireccion: texto(forma.salaVentasDireccion),
    salaVentasTelefono: texto(forma.salaVentasTelefono),
    destacado: forma.destacado,
  });

  const guardar = useMutation({
    mutationFn: () =>
      esNuevo
        ? api<Proyecto>('/admin/proyectos', { metodo: 'POST', cuerpo: cuerpo() })
        : api<Proyecto>(`/admin/proyectos/${id}`, { metodo: 'PUT', cuerpo: cuerpo() }),
    onSuccess: (p) => {
      setError(null);
      setExito('Guardado.');
      void cliente.invalidateQueries({ queryKey: ['proyectos'] });
      if (esNuevo) navegar(`/proyectos/${p.id}`, { replace: true });
      else void cliente.invalidateQueries({ queryKey: ['proyecto', id] });
    },
    onError: (e) => {
      setExito(null);
      setError(e);
    },
  });

  const publicar = useMutation({
    mutationFn: (destino: string) =>
      api<Proyecto>(`/admin/proyectos/${id}/publicacion/${destino}`, { metodo: 'POST' }),
    onSuccess: () => {
      setError(null);
      setExito(null);
      void cliente.invalidateQueries({ queryKey: ['proyecto', id] });
    },
    onError: (e) => setError(e),
  });

  const campo = (nombre: string) =>
    error instanceof ErrorApi ? error.mensajeDe(nombre) : undefined;

  const editable = puede(esNuevo ? 'proyecto:crear' : 'proyecto:editar');

  if (!esNuevo && ficha.isPending) return <Cargando />;

  const proyecto = ficha.data;

  return (
    <>
      <Encabezado
        titulo={esNuevo ? 'Proyecto nuevo' : (proyecto?.nombre ?? 'Proyecto')}
        nota={esNuevo ? 'Primero los datos; las plantas y las unidades, al guardar.' : (proyecto?.constructora ?? undefined)}
      >
        {proyecto && <Etiqueta>{proyecto.estadoPublicacion}</Etiqueta>}
        {!esNuevo &&
          proyecto &&
          puede('proyecto:publicar') &&
          (SIGUIENTES[proyecto.estadoPublicacion] ?? []).map((destino) => (
            <button
              key={destino}
              className="plano"
              disabled={publicar.isPending}
              onClick={() => publicar.mutate(destino)}
            >
              {destino === 'PUBLICADO' ? 'Publicar' : destino.replaceAll('_', ' ').toLowerCase()}
            </button>
          ))}
      </Encabezado>

      <Aviso error={error} exito={exito} />

      <Tarjeta titulo="El proyecto">
        <Campo
          etiqueta="Nombre"
          requerido
          valor={forma.nombre}
          alCambiar={(v) => setForma({ ...forma, nombre: v })}
          error={campo('nombre')}
        />
        <div style={{ height: 12 }} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Campo
            etiqueta="Constructora"
            valor={forma.constructora}
            alCambiar={(v) => setForma({ ...forma, constructora: v })}
          />
          <Campo
            etiqueta="Entrega estimada"
            tipo="date"
            valor={forma.fechaEntregaEstimada}
            alCambiar={(v) => setForma({ ...forma, fechaEntregaEstimada: v })}
          />
          <Seleccion
            etiqueta="Oficina"
            requerido
            valor={forma.sedeId}
            alCambiar={(v) => setForma({ ...forma, sedeId: v })}
            vacio="Elige una oficina"
            opciones={(sedes.data ?? []).map((s) => ({ valor: s.id, texto: s.nombre }))}
            error={campo('sedeId')}
          />
          <Seleccion
            etiqueta="Ciudad"
            requerido
            valor={forma.ciudadId}
            alCambiar={(v) => setForma({ ...forma, ciudadId: v })}
            vacio="Elige una ciudad"
            opciones={(ciudades.data ?? []).map((c) => ({ valor: c.id, texto: c.nombre }))}
            error={campo('ciudadId')}
          />
        </div>
        <div style={{ height: 12 }} />
        <AreaTexto
          etiqueta="Descripción"
          valor={forma.descripcion}
          alCambiar={(v) => setForma({ ...forma, descripcion: v })}
        />
      </Tarjeta>

      <div style={{ height: 16 }} />

      <Tarjeta titulo="Dónde está">
        <Campo
          etiqueta="Dirección"
          valor={forma.direccion}
          alCambiar={(v) => setForma({ ...forma, direccion: v })}
        />
        <div style={{ height: 12 }} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Campo
            etiqueta="Latitud"
            valor={forma.latitud}
            alCambiar={(v) => setForma({ ...forma, latitud: v })}
          />
          <Campo
            etiqueta="Longitud"
            valor={forma.longitud}
            alCambiar={(v) => setForma({ ...forma, longitud: v })}
          />
        </div>
        <div style={{ height: 12 }} />
        <Suspense fallback={<div style={{ height: 320 }}><Cargando /></div>}>
          <SelectorUbicacion
            latitud={forma.latitud}
            longitud={forma.longitud}
            editable={editable}
            alElegir={(latitud, longitud) => setForma((p) => ({ ...p, latitud, longitud }))}
          />
        </Suspense>
        <div style={{ height: 12 }} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Campo
            etiqueta="Sala de ventas"
            valor={forma.salaVentasDireccion}
            alCambiar={(v) => setForma({ ...forma, salaVentasDireccion: v })}
          />
          <Campo
            etiqueta="Teléfono de la sala"
            valor={forma.salaVentasTelefono}
            alCambiar={(v) => setForma({ ...forma, salaVentasTelefono: v })}
          />
        </div>
        <div style={{ height: 12 }} />
        <Casilla
          etiqueta="Destacado en el portal"
          valor={forma.destacado}
          alCambiar={(v) => setForma({ ...forma, destacado: v })}
        />
      </Tarjeta>

      <div style={{ height: 16 }} />
      <button
        disabled={!editable || guardar.isPending || forma.nombre.trim() === ''}
        onClick={() => guardar.mutate()}
      >
        {guardar.isPending ? 'Guardando…' : 'Guardar'}
      </button>

      {!esNuevo && id && (
        <>
          <div style={{ height: 24 }} />
          <Tipologias proyectoId={id} />
        </>
      )}
    </>
  );
}

/* ── Plantas y generación de unidades ──────────────────────────────────── */

interface FormaTipologia {
  nombre: string;
  areaConstruida: string;
  habitaciones: string;
  banos: string;
  garajes: string;
}

const TIPOLOGIA_VACIA: FormaTipologia = {
  nombre: '',
  areaConstruida: '',
  habitaciones: '',
  banos: '',
  garajes: '',
};

function Tipologias({ proyectoId }: { proyectoId: string }) {
  const { puede } = useSesion();
  const cliente = useQueryClient();
  const [abierta, setAbierta] = useState(false);
  const [forma, setForma] = useState<FormaTipologia>(TIPOLOGIA_VACIA);
  const [generando, setGenerando] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [exito, setExito] = useState<string | null>(null);

  const listado = useQuery({
    queryKey: ['tipologias', proyectoId],
    queryFn: () => api<Tipologia[]>(`/admin/proyectos/${proyectoId}/tipologias`),
  });

  const unidades = useQuery({
    queryKey: ['unidades', proyectoId],
    queryFn: () => api<Pagina<Unidad>>(`/admin/proyectos/${proyectoId}/unidades?size=8`),
  });

  const refrescar = () => {
    void cliente.invalidateQueries({ queryKey: ['tipologias', proyectoId] });
    void cliente.invalidateQueries({ queryKey: ['unidades', proyectoId] });
  };

  const crear = useMutation({
    mutationFn: () =>
      api<Tipologia>(`/admin/proyectos/${proyectoId}/tipologias`, {
        metodo: 'POST',
        cuerpo: {
          nombre: forma.nombre.trim(),
          areaConstruida: numero(forma.areaConstruida),
          habitaciones: numero(forma.habitaciones),
          banos: numero(forma.banos),
          garajes: numero(forma.garajes),
          activa: true,
        },
      }),
    onSuccess: () => {
      setForma(TIPOLOGIA_VACIA);
      setAbierta(false);
      setError(null);
      setExito('Planta añadida.');
      refrescar();
    },
    onError: (e) => {
      setExito(null);
      setError(e);
    },
  });

  const editable = puede('proyecto:editar');

  return (
    <Tarjeta titulo="Plantas y unidades">
      <p style={{ opacity: 0.75, fontSize: 14, marginTop: 0 }}>
        Una planta describe un tipo de apartamento: área, habitaciones, baños. Las
        unidades se generan a partir de ella, no se cargan una por una.
      </p>

      <Aviso error={error} exito={exito} />

      {listado.isPending ? (
        <Cargando />
      ) : (listado.data ?? []).length === 0 ? (
        <Vacio>Este proyecto no tiene plantas todavía.</Vacio>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {(listado.data ?? []).map((t) => (
            <div
              key={t.id}
              style={{ border: '1px solid var(--linea)', borderRadius: 8, padding: 12 }}
            >
              <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
                <b>{t.nombre}</b>
                <small style={{ opacity: 0.75 }}>
                  {[
                    t.areaConstruida != null ? `${t.areaConstruida} m²` : null,
                    t.habitaciones != null ? `${t.habitaciones} hab` : null,
                    t.banos != null ? `${t.banos} baños` : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </small>
                {t.totalUnidades != null && (
                  <small style={{ opacity: 0.6 }}>
                    · {t.totalUnidades} unidades
                    {t.unidadesDisponibles != null && `, ${t.unidadesDisponibles} disponibles`}
                  </small>
                )}
              </div>

              {editable && (
                <div style={{ marginTop: 10 }}>
                  <button
                    className="plano"
                    style={{ paddingLeft: 0 }}
                    onClick={() => setGenerando(generando === t.id ? null : t.id)}
                  >
                    {generando === t.id ? 'Cancelar' : 'Generar unidades'}
                  </button>
                  {generando === t.id && (
                    <GenerarUnidades
                      proyectoId={proyectoId}
                      tipologiaId={t.id}
                      alTerminar={(cuantas) => {
                        setGenerando(null);
                        setError(null);
                        setExito(`${cuantas} unidades creadas.`);
                        refrescar();
                      }}
                      alFallar={(e) => {
                        setExito(null);
                        setError(e);
                      }}
                    />
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {editable && (
        <>
          <div style={{ height: 14 }} />
          <button className="plano" style={{ paddingLeft: 0 }} onClick={() => setAbierta(!abierta)}>
            {abierta ? 'Cancelar' : 'Añadir una planta'}
          </button>
        </>
      )}

      {abierta && (
        <div style={{ marginTop: 12 }}>
          <Campo
            etiqueta="Nombre"
            requerido
            ayuda="Como se conoce en la sala de ventas: «Tipo A», «Esquinero»…"
            valor={forma.nombre}
            alCambiar={(v) => setForma({ ...forma, nombre: v })}
          />
          <div style={{ height: 12 }} />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
            <Campo
              etiqueta="Área (m²)"
              valor={forma.areaConstruida}
              alCambiar={(v) => setForma({ ...forma, areaConstruida: v })}
            />
            <Campo
              etiqueta="Habitaciones"
              valor={forma.habitaciones}
              alCambiar={(v) => setForma({ ...forma, habitaciones: v })}
            />
            <Campo
              etiqueta="Baños"
              valor={forma.banos}
              alCambiar={(v) => setForma({ ...forma, banos: v })}
            />
            <Campo
              etiqueta="Garajes"
              valor={forma.garajes}
              alCambiar={(v) => setForma({ ...forma, garajes: v })}
            />
          </div>
          <div style={{ height: 12 }} />
          <button disabled={forma.nombre.trim() === '' || crear.isPending} onClick={() => crear.mutate()}>
            {crear.isPending ? 'Guardando…' : 'Guardar planta'}
          </button>
        </div>
      )}

      {(unidades.data?.content ?? []).length > 0 && (
        <div style={{ marginTop: 18, paddingTop: 14, borderTop: '1px solid var(--linea)' }}>
          <b style={{ fontSize: 14 }}>
            Unidades ({unidades.data?.totalElements ?? 0})
          </b>
          <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
            {(unidades.data?.content ?? []).map((u) => (
              <div key={u.id} style={{ fontSize: 14, display: 'flex', gap: 10 }}>
                <code style={{ opacity: 0.7 }}>{u.codigo}</code>
                <span>{u.titulo}</span>
                {u.precio != null && (
                  <span style={{ marginLeft: 'auto', fontVariantNumeric: 'tabular-nums' }}>
                    {pesos(u.precio)}
                  </span>
                )}
              </div>
            ))}
          </div>
          {(unidades.data?.totalElements ?? 0) > 8 && (
            <small style={{ opacity: 0.6 }}>
              Se muestran las primeras 8. El resto está en Inmuebles.
            </small>
          )}
        </div>
      )}
    </Tarjeta>
  );
}

function GenerarUnidades({
  proyectoId,
  tipologiaId,
  alTerminar,
  alFallar,
}: {
  proyectoId: string;
  tipologiaId: string;
  alTerminar: (cuantas: number) => void;
  alFallar: (error: unknown) => void;
}) {
  const [torre, setTorre] = useState('');
  const [pisoDesde, setPisoDesde] = useState('1');
  const [pisoHasta, setPisoHasta] = useState('10');
  const [porPiso, setPorPiso] = useState('4');
  const [operacion, setOperacion] = useState('VENTA');
  const [precioBase, setPrecioBase] = useState('');
  const [incremento, setIncremento] = useState('');

  const generar = useMutation({
    mutationFn: () =>
      api<Unidad[]>(`/admin/proyectos/${proyectoId}/tipologias/${tipologiaId}/unidades`, {
        metodo: 'POST',
        cuerpo: {
          torre: texto(torre),
          pisoDesde: numero(pisoDesde),
          pisoHasta: numero(pisoHasta),
          unidadesPorPiso: numero(porPiso),
          operacion,
          precioBase: numero(precioBase),
          incrementoPorPiso: numero(incremento),
        },
      }),
    onSuccess: (creadas) => alTerminar(creadas.length),
    onError: alFallar,
  });

  const cuantas =
    (Number(pisoHasta) - Number(pisoDesde) + 1) * Number(porPiso);
  const previsible = Number.isFinite(cuantas) && cuantas > 0;

  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
        <Campo etiqueta="Torre" ayuda="Opcional." valor={torre} alCambiar={setTorre} />
        <Campo etiqueta="Piso desde" valor={pisoDesde} alCambiar={setPisoDesde} requerido />
        <Campo etiqueta="Piso hasta" valor={pisoHasta} alCambiar={setPisoHasta} requerido />
        <Campo etiqueta="Unidades por piso" valor={porPiso} alCambiar={setPorPiso} requerido />
      </div>
      <div style={{ height: 12 }} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
        <Seleccion
          etiqueta="Operación"
          valor={operacion}
          alCambiar={setOperacion}
          opciones={OPERACIONES}
        />
        <Campo etiqueta="Precio base" valor={precioBase} alCambiar={setPrecioBase} requerido />
        <Campo
          etiqueta="Incremento por piso"
          ayuda="Lo que sube el precio por cada piso de altura."
          valor={incremento}
          alCambiar={setIncremento}
        />
      </div>

      <div style={{ height: 12 }} />
      <p style={{ fontSize: 14, opacity: 0.75, margin: 0 }}>
        {previsible
          ? `Se crearán ${cuantas} unidades, cada una con su ficha propia.`
          : 'Revisa los pisos y las unidades por piso.'}
      </p>

      <div style={{ height: 10 }} />
      <button
        disabled={!previsible || precioBase.trim() === '' || generar.isPending}
        onClick={() => generar.mutate()}
      >
        {generar.isPending ? 'Creando…' : 'Generar'}
      </button>
    </div>
  );
}
